import type { ComponentInstance } from "building-types/components/componentInstanceTypes";
import type { Doc } from "building-types/doc";
import type { RWTable, Table } from "common/data/table";
import { notUndefinedOrNull } from "common/graph/entityGraph";
import { parseEntityInfoFromHandleHash } from "doc/entity/docEntityHandleHash";
import {
  docGetAssociatedEntityIdsForComponent,
  docGetComponentIdsForAssociatedEntity,
} from "doc/options/componentAssociations";
import { buildInstanceOptionSetIdsByArchetype } from "doc/options/instanceOptionSetIndex";
import { getReportableUnitDesignatorLabelIds } from "doc/utils/labelTools";

/** ID used for the root node of a component instance tree */
export const ROOT_INSTANCE_ID = "root";

declare const InstanceIdPathSymbol: unique symbol;

/** Slash-separated path of component instance IDs, e.g. "root/abc123/def456" */
export type InstanceIdPath = string & { [InstanceIdPathSymbol]: never };

/**
 * A node in the component instance tree. Generic T is the payload type:
 * - for options we use `string` optionSetIds here (during build from the doc)
 * - downstream we use mapTreePayload to populate `OptionSetState` or similar
 * - payload intended to be generic to support other types as needed
 */
export interface ComponentInstanceTree<T> {
  /** ROOT_INSTANCE_ID for building root, componentInstanceId otherwise */
  readonly id: string;
  /** Full path of instance IDs from root, e.g., "root/abc123/def456". Used for filtering. */
  readonly path: InstanceIdPath;
  readonly name: string;
  readonly componentId: string | null; // null for root node
  readonly payload: T[];
  readonly children: ComponentInstanceTree<T>[];
  /** The reporting unit this instance belongs to, if any. Used for unit-based filtering. */
  readonly reportingUnitId: string | null;
}

/** Transform tree payloads (e.g., hydrate ID-only tree with full objects) */
export function mapTreePayload<A, B>(
  tree: ComponentInstanceTree<A>,
  mapPayload: (items: A[], node: ComponentInstanceTree<A>) => B[],
): ComponentInstanceTree<B> {
  const { payload, children, ...rest } = tree;
  return {
    ...rest,
    payload: mapPayload(payload, tree),
    children: children.map((child) => mapTreePayload(child, mapPayload)),
  };
}

/**
 * Returns the reporting unit ID for a component instance, if any.
 * Uses labels linked to ReportableUnitDesignations.
 * Returns the labelId of the first matching reportable unit designator label.
 */
export function getReportingUnitIdForInstance(
  instance: ComponentInstance,
  reportingUnitLabelIds: readonly string[],
): string | null {
  for (const labelId of reportingUnitLabelIds) {
    if (instance.labels[labelId]) {
      return labelId;
    }
  }
  return null;
}

/** Parent→children lookups for component instances derived from componentMembers. */
export interface ComponentMembershipLookups {
  // component ID => child instance IDs
  readonly componentIdToMemberInstanceIds: Table<string[]>;
  // component ID => child option set IDs
  readonly componentIdToMemberOptionSetIds: Table<string[]>;
  // member ID (instance or option set) => parent component ID, undefined if root-level
  readonly memberIdToParentComponentId: Table<string>;
}

function buildMemberIdToParentComponentId(doc: Doc): Table<string> {
  const memberIdToParent: RWTable<string> = {};

  for (const hash in doc.componentMembers) {
    const componentId = doc.componentMembers[hash];
    const entityInfo = parseEntityInfoFromHandleHash(hash);
    if (entityInfo && componentId) {
      memberIdToParent[entityInfo.entityId] = componentId;
    }
  }

  return memberIdToParent;
}

export function buildComponentMembershipLookups(doc: Doc): ComponentMembershipLookups {
  const memberIdToParentComponentId = buildMemberIdToParentComponentId(doc);
  const componentIdToMemberInstanceIds: RWTable<string[]> = {};
  const componentIdToMemberOptionSetIds: RWTable<string[]> = {};

  for (const [memberId, parentId] of Object.entries(memberIdToParentComponentId)) {
    const isInstance = doc.componentInstances[memberId] !== undefined;
    if (!isInstance) {
      continue; // Entity was deleted or is another type — skip
    }

    componentIdToMemberInstanceIds[parentId] ||= [];
    componentIdToMemberInstanceIds[parentId].push(memberId);
  }

  for (const componentId of Object.keys(doc.components)) {
    const optionSetIds = docGetAssociatedEntityIdsForComponent(doc, componentId, "OPTION_SET");
    if (optionSetIds.length > 0) {
      componentIdToMemberOptionSetIds[componentId] = optionSetIds;
    }
  }

  // Sort instances by their explicit sortIndex for consistent ordering
  for (const parentId of Object.keys(componentIdToMemberInstanceIds)) {
    componentIdToMemberInstanceIds[parentId] = sortInstanceIdsBySortIndex(
      doc,
      componentIdToMemberInstanceIds[parentId],
    );
  }

  return {
    componentIdToMemberInstanceIds,
    componentIdToMemberOptionSetIds,
    memberIdToParentComponentId,
  };
}

function sortInstanceIdsBySortIndex(doc: Doc, ids: string[]): string[] {
  return ids
    .slice()
    .sort((a, b) => (doc.componentInstances[a]?.sortIndex ?? 0) - (doc.componentInstances[b]?.sortIndex ?? 0));
}

/** Returns true if the instance is a member of a component definition (not root-level on the building). */
function isInComponentDefinition(instanceId: string, memberIdToParentComponentId: Table<string>): boolean {
  return memberIdToParentComponentId[instanceId] !== undefined;
}

/** Builds the building tree with option set IDs. Use mapTreePayload to hydrate with full objects. */
export function buildBuildingTree(doc: Doc): ComponentInstanceTree<string> {
  const membership = buildComponentMembershipLookups(doc);
  const instanceOptionSetIdsByComponentInstance = buildInstanceOptionSetIdsForBuildingTree(
    doc,
    membership.memberIdToParentComponentId,
  );
  const reportingUnitLabelIds = getReportableUnitDesignatorLabelIds(doc);

  const rootOptionSetIds = Object.keys(doc.optionSets).filter(
    (optionSetId) => docGetComponentIdsForAssociatedEntity(doc, { type: "OPTION_SET", id: optionSetId }).length === 0,
  );

  const rootInstanceIds = sortInstanceIdsBySortIndex(
    doc,
    Object.keys(doc.componentInstances).filter(
      (instanceId) => !isInComponentDefinition(instanceId, membership.memberIdToParentComponentId),
    ),
  );

  const children = rootInstanceIds
    .map((instanceId) =>
      buildComponentInstanceNodeWithIds({
        instanceId,
        parentPath: ROOT_INSTANCE_ID,
        parentReportingUnitId: undefined,
        doc,
        instanceOptionSetIdsByComponentInstance,
        membership,
        reportingUnitLabelIds,
      }),
    )
    .filter(notUndefinedOrNull);

  return {
    id: ROOT_INSTANCE_ID,
    path: ROOT_INSTANCE_ID as InstanceIdPath,
    name: "Building",
    componentId: null,
    payload: rootOptionSetIds,
    children,
    reportingUnitId: null,
  };
}

/**
 * Builds lookup of component instance ID → instance option set IDs for the building tree.
 * Excludes instance option sets targeting instances inside component definitions.
 */
function buildInstanceOptionSetIdsForBuildingTree(
  doc: Doc,
  memberIdToParentComponentId: Table<string>,
): Table<string[]> {
  const result: RWTable<string[]> = {};
  const sortIndexByInstanceOptionSetId: RWTable<number> = {};

  for (const instanceOptionSet of Object.values(doc.instanceOptionSets)) {
    // Skip instance option sets targeting instances inside component definitions
    if (isInComponentDefinition(instanceOptionSet.componentInstanceId, memberIdToParentComponentId)) {
      continue;
    }

    (result[instanceOptionSet.componentInstanceId] ||= []).push(instanceOptionSet.id);
    sortIndexByInstanceOptionSetId[instanceOptionSet.id] = instanceOptionSet.sortIndex ?? 0;
  }

  for (const ids of Object.values(result)) {
    ids.sort((a, b) => (sortIndexByInstanceOptionSetId[a] ?? 0) - (sortIndexByInstanceOptionSetId[b] ?? 0));
  }

  return result;
}

interface BuildComponentInstanceNodeWithIdsProps {
  readonly instanceId: string;
  readonly parentPath: string;
  // undefined means "no parent" (top-level/root child), null means "parent explicitly has no reporting unit"
  readonly parentReportingUnitId: string | null | undefined;
  readonly doc: Doc;
  readonly instanceOptionSetIdsByComponentInstance: Table<string[]>;
  readonly membership: ComponentMembershipLookups;
  readonly reportingUnitLabelIds: readonly string[];
}

function buildComponentInstanceNodeWithIds({
  instanceId,
  parentPath,
  parentReportingUnitId,
  doc,
  instanceOptionSetIdsByComponentInstance,
  membership,
  reportingUnitLabelIds,
}: BuildComponentInstanceNodeWithIdsProps): ComponentInstanceTree<string> | undefined {
  const componentInstance = doc.componentInstances[instanceId];
  if (!componentInstance) {
    return undefined;
  }

  const path = `${parentPath}/${instanceId}` as InstanceIdPath;
  const optionSetIds = instanceOptionSetIdsByComponentInstance[instanceId] ?? [];
  // Inherit parent's reporting unit for descendants. Only top-level instances (no parent provided)
  // resolve reporting unit from tags.
  const reportingUnitId =
    parentReportingUnitId === undefined
      ? getReportingUnitIdForInstance(componentInstance, reportingUnitLabelIds)
      : parentReportingUnitId;

  const memberInstanceIds = membership.componentIdToMemberInstanceIds[componentInstance.componentId] ?? [];
  const children = memberInstanceIds
    .map((memberId) =>
      buildComponentInstanceNodeWithIds({
        instanceId: memberId,
        parentPath: path,
        parentReportingUnitId: reportingUnitId,
        doc,
        instanceOptionSetIdsByComponentInstance,
        membership,
        reportingUnitLabelIds,
      }),
    )
    .filter(notUndefinedOrNull);

  return {
    id: instanceId,
    path,
    name: componentInstance.name,
    componentId: componentInstance.componentId,
    payload: optionSetIds,
    children,
    reportingUnitId,
  };
}

// -----------------------------------------------------------------------------
// Component Definition Tree
// -----------------------------------------------------------------------------

/** Builds a tree for a single component definition (for Studio). */
export function buildComponentDefinitionTree(doc: Doc, componentId: string): ComponentInstanceTree<string> | undefined {
  const component = doc.components?.[componentId];
  if (!component) {
    return undefined;
  }

  const membership = buildComponentMembershipLookups(doc);
  const memberOptionSetIds = membership.componentIdToMemberOptionSetIds[componentId] ?? [];
  const memberInstanceIds = membership.componentIdToMemberInstanceIds[componentId] ?? [];

  if (memberOptionSetIds.length === 0 && memberInstanceIds.length === 0) {
    return undefined;
  }

  // Only include instance option sets from option sets that belong to this component
  const instanceOptionSetIdsByComponentInstance = buildInstanceOptionSetIdsForComponent(doc, memberOptionSetIds);

  const children = memberInstanceIds
    .map((memberId) =>
      buildComponentInstanceNodeWithIds({
        instanceId: memberId,
        parentPath: componentId,
        parentReportingUnitId: null,
        doc,
        instanceOptionSetIdsByComponentInstance,
        membership,
        reportingUnitLabelIds: [], // Not relevant for component definitions (only building-level)
      }),
    )
    .filter(notUndefinedOrNull);

  return {
    id: componentId,
    path: componentId as InstanceIdPath,
    name: component.name,
    componentId,
    payload: memberOptionSetIds,
    children,
    reportingUnitId: null,
  };
}

/**
 * Builds lookup of component instance ID → instance option set IDs for a component definition.
 * Only includes instance option sets from option sets that belong to this component.
 */
function buildInstanceOptionSetIdsForComponent(doc: Doc, memberOptionSetIds: string[]): Table<string[]> {
  const result: RWTable<string[]> = {};
  const sortIndexByInstanceOptionSetId: RWTable<number> = {};
  const instanceOptionSetIdsByArchetype = buildInstanceOptionSetIdsByArchetype(doc.instanceOptionSets);

  for (const optionSetId of memberOptionSetIds) {
    if (!doc.optionSets[optionSetId]) {
      continue;
    }

    const instanceOptionSetIds = instanceOptionSetIdsByArchetype[optionSetId];
    if (!instanceOptionSetIds) {
      continue;
    }

    for (const instanceOptionSetId of instanceOptionSetIds) {
      const instanceOptionSet = doc.instanceOptionSets[instanceOptionSetId];
      (result[instanceOptionSet.componentInstanceId] ||= []).push(instanceOptionSetId);
      sortIndexByInstanceOptionSetId[instanceOptionSetId] = instanceOptionSet.sortIndex ?? 0;
    }
  }

  for (const ids of Object.values(result)) {
    ids.sort((a, b) => (sortIndexByInstanceOptionSetId[a] ?? 0) - (sortIndexByInstanceOptionSetId[b] ?? 0));
  }

  return result;
}
